import {
  GetCancellationsByUserIdQuery,
  GetRenewalsByUserIdQuery,
  GetUserQuery,
  GetUsersQuery,
  Users_Order_By,
} from "@/api/hasura/generated/graphql"
import { OtpResponse } from "@/api/backofficeRest/generated/model"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { UserParser } from "@/models/admin/user/user.parser"
import { IUser } from "@/models/admin/user/user.types"
import { UserCancellationParser } from "@/models/admin/userCancellation/userCancellation.parser"
import { UserRenewalParser } from "@/models/admin/userRenewal/userRenewal.parser"
import { HasuraQueries } from "@/api/hasura/queries"
import { ICustomer } from "@/models/customer/customer.types"
import { UserMutator } from "@/mutators/user/UserMutator"
import { CookieService } from "@/services/CookieService"
import { rootDomain } from "@/helpers/stringHelpers"

const AUTO_LOGIN_COOKIE_NAME = "myaccount-auto_login_credential"
const AUTO_LOGIN_QUERY_PARAM = "auto-login-from-cookie"

export type CustomerList = {
  total: number
  items: ICustomer[]
}

export type UserListResult = Omit<GetUsersQuery, "users"> & { users: IUser[] }

class UserServiceClass {
  getUsersForAccount(accountId: number | null) {
    return useQuery({
      ...HasuraQueries.user.getUsersForAccount(accountId ?? -1),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) => {
        return (
          data.billing_account_by_pk?.customer_user_rels.flatMap((rel) =>
            rel.user && !rel.user.deleted
              ? [UserParser.toDomain(rel.user)]
              : [],
          ) ?? []
        )
      },
      enabled: !!accountId,
    })
  }

  getUserList(
    search: string,
    page: number,
    pageSize: number,
    orderBy: Users_Order_By,
  ): QueryResult<UserListResult> {
    return useQuery({
      ...HasuraQueries.user.list(
        `%${search}%`,
        (page - 1) * pageSize,
        pageSize,
        orderBy,
      ),
      placeholderData: {
        users: [],
        users_aggregate: {
          aggregate: {
            count: 0,
          },
        },
      },
      useRawResponse: true,
      parserFn: (data) => ({
        ...data,
        users: data.users.map(UserParser.toDomain),
      }),
    })
  }

  getUser(userId: number) {
    return useQuery({
      ...HasuraQueries.user.details(userId),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data: GetUserQuery) => {
        if (!data.users_by_pk) return null
        return UserParser.toDomain(data.users_by_pk)
      },
    })
  }

  getCancellationsByUserId(userId: number) {
    return useQuery({
      ...HasuraQueries.user.details(userId)._ctx.cancellations,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetCancellationsByUserIdQuery) => {
        return data.cancellations.map(UserCancellationParser.toDomain)
      },
    })
  }

  getRenewalsByUserId(userId: number) {
    return useQuery({
      ...HasuraQueries.user.details(userId)._ctx.renewals,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetRenewalsByUserIdQuery) => {
        return data.renewal.map(UserRenewalParser.toDomain)
      },
    })
  }

  async getOtp(userId: number, copyToClipboard = false): Promise<string> {
    const { mutateAsync } = UserMutator.generateOtp()

    const response = (await mutateAsync({ userId })) as OtpResponse

    if (!response?.password) throw new Error("Failed to generate OTP")

    if (copyToClipboard) {
      await navigator.clipboard.writeText(response.password)
    }

    return response.password
  }

  async signInToMyAccount(userId: number, email: string) {
    const myAccountUrl = import.meta.env.SO_MY_ACCOUNT_URL
    if (!myAccountUrl) throw new Error("My Account URL not defined")

    const otp = await this.getOtp(userId)
    const cookieData = { email, otp }
    const domain = rootDomain(new URL(myAccountUrl).hostname)

    CookieService.set(AUTO_LOGIN_COOKIE_NAME, cookieData, {
      maxAge: 5 * 60, // 5 minutes
      domain: `.${domain}`,
      path: "/",
      secure: true,
    })

    window.open(`${myAccountUrl}?${AUTO_LOGIN_QUERY_PARAM}`, "_blank")
  }

  isEmailAvailable(email: string) {
    return useQuery({
      ...HasuraQueries.user.list(email, 0, 0, {}),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data: GetUsersQuery) => {
        return data.users_aggregate?.aggregate?.count === 0
      },
    })
  }
}

export const UserService = new UserServiceClass()
